import React, { useEffect, useState } from "react";
import { getApi, saveApi } from "../../../../helpers";
import BasePopup from "../../../../components/Popup/BasePopup"; // Create a base popup component without Ant Design
import "./styles.css"; // Create a CSS file for styling

const extraFieldOptions = [
  "country of origin",
  "flavour",
  "color",
  "manufacture date",
  "expiry date",
  "expiry",
];

export default function ProductPopup({
  visible = false,
  title = "Add Product",
  data = {},
  categoryOptions = [],
  storeOptions = [],
  cancelHandler,
  saveHandler,
  updateProduct = false,
}) {
  // Form state
  const [formValues, setFormValues] = useState(data);
  const [formErrors, setFormErrors] = useState({});
  const [extraFields, setExtraFields] = useState(data.meta?.extra_field || []);
  const [variants, setVariants] = useState([{ variant_active: true }]);

  // Other state variables
  const [selectedStore, setSelectedStore] = useState({});
  const [loading, setLoading] = useState(false);
  const [categoryId, setCategoryId] = useState(null);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [subCategoryId, setSubCategoryId] = useState(null);
  const [subCategoryGroupOptions, setSubCategoryGroupOptions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [buttonLabel, setButtonLabel] = useState("Next");
  const [newProduct, setNewProduct] = useState({});
  const [popupTitle, setPopupTitle] = useState("");

  useEffect(() => {
    if (updateProduct && data.name) {
      setFormValues(data);
      setButtonLabel("Save");
      if (data.category_id) getSubCategoryOptions(data.category_id);
      if (data.sub_category_id)
        getSubCategoryGroupOptions(data.sub_category_id);
      if (data.meta?.extra_field) setExtraFields(data.meta.extra_field);
    }
  }, []);

  const getSubCategoryOptions = async (category_id = 0) => {
    setCategoryId(category_id);
    setLoading(true);
    const res = await getApi({ category_id: category_id }, "getSubCategory");
    if (res.data) setSubCategoryOptions(res.data);
    setLoading(false);
  };

  const getSubCategoryGroupOptions = async (sub_category_id = 0) => {
    setSubCategoryId(sub_category_id);
    setLoading(true);
    const res = await getApi(
      { sub_category_id: sub_category_id },
      "getSubCategoryGroup"
    );
    if (res.data && res.data.length) {
      res.data.shift();
      setSubCategoryGroupOptions(res.data);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSelectChange = (e, isCategory = false, isSubCategory = false) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });

    if (isCategory) {
      getSubCategoryOptions(value);
    } else if (isSubCategory) {
      getSubCategoryGroupOptions(value);
    }
  };

  const handleStoreSelect = (e) => {
    const selectedStoreId = e.target.value;
    const selectedStoreName = e.target.options[e.target.selectedIndex].text;

    if (selectedStoreId) {
      setSelectedStore({
        value: selectedStoreId,
        label: selectedStoreName,
      });
    } else {
      setSelectedStore({});
    }
  };

  const addExtraField = () => {
    setExtraFields([...extraFields, { key: "", value: "" }]);
  };

  const removeExtraField = (index) => {
    const updatedFields = [...extraFields];
    updatedFields.splice(index, 1);
    setExtraFields(updatedFields);
  };

  const handleExtraFieldChange = (index, field, value) => {
    const updatedFields = [...extraFields];
    updatedFields[index][field] = value;
    setExtraFields(updatedFields);
  };

  const addVariant = () => {
    setVariants([...variants, { variant_active: true }]);
  };

  const removeVariant = (index) => {
    if (index === 0) return; // Don't remove the first variant
    const updatedVariants = [...variants];
    updatedVariants.splice(index, 1);
    setVariants(updatedVariants);
  };

  const handleVariantChange = (index, field, value) => {
    const updatedVariants = [...variants];
    updatedVariants[index][field] = value;
    setVariants(updatedVariants);
  };

  const validateForm = () => {
    const errors = {};

    if (!formValues.name) {
      errors.name = "Please enter the name of product!";
    }

    if (!formValues.category_id) {
      errors.category_id = "Please select the category of product!";
    }

    if (!formValues.sub_category_id) {
      errors.sub_category_id = "Please select the sub category of product!";
    }

    // Add validation for variants if on step 1
    if (current === 1) {
      variants.forEach((variant, index) => {
        if (!variant.variant_name) {
          if (!errors.variants) errors.variants = {};
          if (!errors.variants[index]) errors.variants[index] = {};
          errors.variants[index].variant_name = "Unit is required";
        }

        if (!variant.variant_sku) {
          if (!errors.variants) errors.variants = {};
          if (!errors.variants[index]) errors.variants[index] = {};
          errors.variants[index].variant_sku = "SKU is required";
        }

        // Validate numeric fields
        if (variant.weight && isNaN(parseFloat(variant.weight))) {
          if (!errors.variants) errors.variants = {};
          if (!errors.variants[index]) errors.variants[index] = {};
          errors.variants[index].weight = "Weight should be a number";
        }
      });
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveCategory = async (values) => {
    setLoading(true);
    let msg = "Added Category";
    let apiName = "createCategory";
    if (popupTitle === "Add Sub Category") {
      msg = "Added Sub Category";
      apiName = "createSubCategory";
      values = { ...values, category_id: categoryId };
    } else if (popupTitle === "Add Sub Category Group") {
      msg = "Added Sub Category Group";
      apiName = "createSubCategoryGroup";
      values = { ...values, sub_category_id: subCategoryId };
    }
    values = { ...values, getId: true };
    const res = await saveApi(values, msg, apiName);
    setPopupTitle("");

    // Handle the response based on category type
    if (res.category_id) {
      categoryOptions.unshift({
        name: values.name,
        category_id: res.category_id,
      });
    } else if (res.sub_category_id) {
      let subCategories = subCategoryOptions;
      subCategories.unshift({
        sub_category_name: values.name,
        sub_category_id: res.sub_category_id,
      });
      setSubCategoryOptions(subCategories);
    } else if (res.sub_category_group_id) {
      let subCategoryGroups = subCategoryGroupOptions;
      subCategoryGroups.unshift({
        sub_category_group_name: values.name,
        sub_category_group_id: res.sub_category_group_id,
      });
      setSubCategoryGroupOptions(subCategoryGroups);
    }
    setLoading(false);
  };

  const saveProduct = () => {
    if (validateForm()) {
      if (current === 0) {
        // Process basic details
        setCurrent(1);
        let productData = { ...formValues };

        if (!productData.active) {
          productData = { ...productData, is_active: 0 };
        }

        if (extraFields.length > 0) {
          productData = { ...productData, meta: { extra_field: extraFields } };
        }

        // Add category name
        if (productData.category_id) {
          categoryOptions.forEach((category) => {
            if (category.category_id === parseInt(productData.category_id)) {
              productData = { ...productData, category_name: category.name };
            }
          });
        }

        // Add subcategory name
        if (productData.sub_category_id) {
          subCategoryOptions.forEach((sub_category) => {
            if (
              sub_category.sub_category_id ===
              parseInt(productData.sub_category_id)
            ) {
              productData = {
                ...productData,
                sub_category_name: sub_category.sub_category_name,
              };
            }
          });
        }

        // Add subcategory group name
        if (productData.sub_category_group_id) {
          subCategoryGroupOptions.forEach((group) => {
            if (
              group.sub_category_group_id ===
              parseInt(productData.sub_category_group_id)
            ) {
              productData = {
                ...productData,
                sub_category_group_name: group.sub_category_group_name,
              };
            }
          });
        }

        setNewProduct(productData);

        if (updateProduct) {
          saveHandler(productData);
        }
      } else if (current === 1) {
        // Process variants
        let processedVariants = variants.map((variant) => {
          const weightInGrams =
            variant.unit === "g"
              ? parseFloat(variant.weight)
              : parseFloat(variant.weight) * 1000;

          return {
            ...variant,
            variant_is_active: variant.variant_active ? 1 : 0,
            weight: weightInGrams,
          };
        });

        let finalProduct = { ...newProduct, variants: processedVariants };

        if (selectedStore.value) {
          finalProduct = { ...finalProduct, store_id: selectedStore.value };
        }

        setNewProduct(finalProduct);
        setButtonLabel("Save");
        setCurrent(2);
      } else {
        saveHandler(newProduct);
      }
    }
  };

  const renderSteps = () => {
    return (
      <div className="steps-container">
        <div className={`step ${current >= 0 ? "active" : ""}`}>
          <div className="step-number">1</div>
          <div className="step-title">Basic Details</div>
        </div>
        <div className="step-line"></div>
        <div className={`step ${current >= 1 ? "active" : ""}`}>
          <div className="step-number">2</div>
          <div className="step-title">Extra Details</div>
        </div>
        <div className="step-line"></div>
        <div className={`step ${current >= 2 ? "active" : ""}`}>
          <div className="step-number">3</div>
          <div className="step-title">Preview</div>
        </div>
      </div>
    );
  };

  const renderBasicDetailsForm = () => {
    return (
      <div className="form-section">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">
              Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className={formErrors.name ? "input-error" : ""}
              value={formValues.name || ""}
              onChange={handleInputChange}
              placeholder="Ex. Cow Milk"
            />
            {formErrors.name && (
              <div className="error-message">{formErrors.name}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="category_id">
              Category <span className="required">*</span>
            </label>
            <div className="select-with-button">
              <select
                id="category_id"
                name="category_id"
                className={formErrors.category_id ? "input-error" : ""}
                value={formValues.category_id || ""}
                onChange={(e) => handleSelectChange(e, true, false)}
              >
                <option value="">Select a category</option>
                {categoryOptions.map((category) => (
                  <option
                    key={category.category_id}
                    value={category.category_id}
                  >
                    {category.name}
                  </option>
                ))}
              </select>
              {/* <button
                type="button"
                className="add-button"
                onClick={() => setPopupTitle("Add Category")}
              >
                +
              </button> */}
            </div>
            {formErrors.category_id && (
              <div className="error-message">{formErrors.category_id}</div>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="sub_category_id">
              Sub Category <span className="required">*</span>
            </label>
            <div className="select-with-button">
              <select
                id="sub_category_id"
                name="sub_category_id"
                className={formErrors.sub_category_id ? "input-error" : ""}
                value={formValues.sub_category_id || ""}
                onChange={(e) => handleSelectChange(e, false, true)}
                disabled={loading || !categoryId}
              >
                <option value="">Select a sub category</option>
                {subCategoryOptions.map((subcategory) => (
                  <option
                    key={subcategory.sub_category_id}
                    value={subcategory.sub_category_id}
                  >
                    {subcategory.sub_category_name}
                  </option>
                ))}
              </select>
              {/* <button
                type="button"
                className="add-button"
                onClick={() =>
                  categoryId
                    ? setPopupTitle("Add Sub Category")
                    : showToast("Select category of product first!")
                }
              >
                +
              </button> */}
            </div>
            {formErrors.sub_category_id && (
              <div className="error-message">{formErrors.sub_category_id}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="sub_category_group_id">Sub Category Group</label>
            <div className="select-with-button">
              <select
                id="sub_category_group_id"
                name="sub_category_group_id"
                value={formValues.sub_category_group_id || ""}
                onChange={handleInputChange}
                disabled={loading || !subCategoryId}
              >
                <option value="">Select a group</option>
                {subCategoryGroupOptions.map((group) => (
                  <option
                    key={group.sub_category_group_id}
                    value={group.sub_category_group_id}
                  >
                    {group.sub_category_group_name}
                  </option>
                ))}
              </select>
              {/* <button
                type="button"
                className="add-button"
                onClick={() =>
                  subCategoryId
                    ? setPopupTitle("Add Group")
                    : showToast("Select sub category of product first!")
                }
              >
                +
              </button> */}
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              rows="3"
              value={formValues.description || ""}
              onChange={handleInputChange}
              placeholder="Ex. 100% Pure Cow Milk."
              maxLength={255}
            ></textarea>
            <div className="char-count">
              {formValues.description?.length || 0}/255
            </div>
          </div>

          {/* Move active-checkbox here, after description */}
          <div className="active-checkbox">
            <input
              type="checkbox"
              name="active"
              checked={
                formValues.active === undefined
                  ? data.is_active !== 0
                  : formValues.active
              }
              onChange={handleInputChange}
            />
            <label htmlFor="active">Active</label>
          </div>
        </div>

        <div className="divider">
          <span>Extra Fields</span>
        </div>

        {extraFields.map((field, index) => (
          <div className="form-row extra-field" key={index}>
            <div className="form-group">
              <input
                type="text"
                list="extraFieldOptions"
                value={field.key || ""}
                onChange={(e) =>
                  handleExtraFieldChange(index, "key", e.target.value)
                }
                placeholder="Ex. Expiry"
                className="extra-field-key"
              />
              <datalist id="extraFieldOptions">
                {extraFieldOptions.map((option, i) => (
                  <option key={i} value={option} />
                ))}
              </datalist>
            </div>

            <div className="form-group">
              <input
                type="text"
                value={field.value || ""}
                onChange={(e) =>
                  handleExtraFieldChange(index, "value", e.target.value)
                }
                placeholder="Ex. 7 days"
                className="extra-field-value"
              />
            </div>

            <button
              type="button"
              className="remove-button"
              onClick={() => removeExtraField(index)}
            >
              ×
            </button>
          </div>
        ))}

        <button
          type="button"
          className="add-field-button"
          onClick={addExtraField}
        >
          + Add field
        </button>
      </div>
    );
  };

  const renderVariantsForm = () => {
    return (
      <div className="variants-section">
        {variants.map((variant, index) => (
          <div key={index} className="variant-container">
            <div className="variant-header">
              <h3>{index === 0 ? "Extra Details" : `Variant ${index}`}</h3>

              <div className="variant-actions">
                <label className="variant-active">
                  <input
                    type="checkbox"
                    checked={variant.variant_active !== false}
                    onChange={(e) =>
                      handleVariantChange(
                        index,
                        "variant_active",
                        e.target.checked
                      )
                    }
                  />
                  Active
                </label>

                {index > 0 && (
                  <button
                    type="button"
                    className="remove-button"
                    onClick={() => removeVariant(index)}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Unit <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={variant.variant_name || ""}
                  onChange={(e) =>
                    handleVariantChange(index, "variant_name", e.target.value)
                  }
                  placeholder="Ex. 1 Ltr"
                  className={
                    formErrors.variants?.[index]?.variant_name
                      ? "input-error"
                      : ""
                  }
                />
                {formErrors.variants?.[index]?.variant_name && (
                  <div className="error-message">
                    {formErrors.variants[index].variant_name}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>
                  SKU <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={variant.variant_sku || ""}
                  onChange={(e) =>
                    handleVariantChange(index, "variant_sku", e.target.value)
                  }
                  placeholder="Ex. CM001"
                  className={
                    formErrors.variants?.[index]?.variant_sku
                      ? "input-error"
                      : ""
                  }
                />
                {formErrors.variants?.[index]?.variant_sku && (
                  <div className="error-message">
                    {formErrors.variants[index].variant_sku}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group weight-group">
                <label>Weight</label>
                <div className="weight-input">
                  <input
                    type="text"
                    value={variant.weight || ""}
                    onChange={(e) =>
                      handleVariantChange(index, "weight", e.target.value)
                    }
                    placeholder="Ex. 0.5"
                    className={
                      formErrors.variants?.[index]?.weight ? "input-error" : ""
                    }
                  />
                  <select
                    value={variant.unit || "g"}
                    onChange={(e) =>
                      handleVariantChange(index, "unit", e.target.value)
                    }
                  >
                    <option value="g">Gram</option>
                    <option value="kg">Kilogram</option>
                  </select>
                </div>
                {formErrors.variants?.[index]?.weight && (
                  <div className="error-message">
                    {formErrors.variants[index].weight}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Store</label>
                <select
                  value={selectedStore.value || ""}
                  onChange={handleStoreSelect}
                >
                  <option value="">Select a store</option>
                  {storeOptions.map((store) => (
                    <option key={store.store_id} value={store.store_id}>
                      {store.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedStore.value && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Quantity</label>
                    <input
                      type="number"
                      value={variant.variant_stock || ""}
                      onChange={(e) =>
                        handleVariantChange(
                          index,
                          "variant_stock",
                          e.target.value
                        )
                      }
                      placeholder="Ex. 2000"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label>Cost Price</label>
                    <div className="price-input">
                      <span className="currency">₹</span>
                      <input
                        type="number"
                        value={variant.cost_price || ""}
                        onChange={(e) =>
                          handleVariantChange(
                            index,
                            "cost_price",
                            e.target.value
                          )
                        }
                        placeholder="Ex. 89"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Max Retail Price</label>
                    <div className="price-input">
                      <span className="currency">₹</span>
                      <input
                        type="number"
                        value={variant.mrp || ""}
                        onChange={(e) =>
                          handleVariantChange(index, "mrp", e.target.value)
                        }
                        placeholder="Ex. 99"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Sell Price</label>
                    <div className="price-input">
                      <span className="currency">₹</span>
                      <input
                        type="number"
                        value={variant.price || ""}
                        onChange={(e) =>
                          handleVariantChange(index, "price", e.target.value)
                        }
                        placeholder="Ex. 79"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}

        <button
          type="button"
          className="add-variant-button"
          onClick={addVariant}
        >
          + Add Variant
        </button>
      </div>
    );
  };

  const renderPreview = () => {
    return (
      <div className="preview-section">
        <div className="preview-grid">
          <div className="preview-item">
            <div className="preview-label">Name</div>
            <div className="preview-value">{newProduct.name}</div>
          </div>

          <div className="preview-item">
            <div className="preview-label">Category</div>
            <div className="preview-value">{newProduct.category_name}</div>
          </div>

          <div className="preview-item">
            <div className="preview-label">Sub Category</div>
            <div className="preview-value">{newProduct.sub_category_name}</div>
          </div>

          {newProduct.sub_category_group_name && (
            <div className="preview-item">
              <div className="preview-label">Group</div>
              <div className="preview-value">
                {newProduct.sub_category_group_name}
              </div>
            </div>
          )}

          {newProduct.description && (
            <div className="preview-item full-width">
              <div className="preview-label">Description</div>
              <div className="preview-value">{newProduct.description}</div>
            </div>
          )}

          {newProduct.meta?.extra_field &&
            newProduct.meta.extra_field.map((field, index) => (
              <div className="preview-item" key={index}>
                <div className="preview-label text-capitalize">{field.key}</div>
                <div className="preview-value text-capitalize">
                  {field.value}
                </div>
              </div>
            ))}
        </div>

        {newProduct.variants && newProduct.variants.length > 0 && (
          <div className="variants-preview">
            <div className="divider">
              <span>Variants</span>
            </div>

            {newProduct.variants.map((variant, index) => (
              <div key={index} className="variant-preview">
                <h3>Variant {index + 1}</h3>
                <div className="preview-grid">
                  <div className="preview-item">
                    <div className="preview-label">Unit</div>
                    <div className="preview-value">{variant.variant_name}</div>
                  </div>

                  <div className="preview-item">
                    <div className="preview-label">SKU</div>
                    <div className="preview-value">{variant.variant_sku}</div>
                  </div>

                  <div className="preview-item">
                    <div className="preview-label">Weight</div>
                    <div className="preview-value">{variant.weight} g</div>
                  </div>

                  {selectedStore.value && (
                    <>
                      <div className="preview-item">
                        <div className="preview-label">Store</div>
                        <div className="preview-value">
                          {selectedStore.label}
                        </div>
                      </div>

                      <div className="preview-item">
                        <div className="preview-label">Quantity</div>
                        <div className="preview-value">
                          {variant.variant_stock}
                        </div>
                      </div>

                      <div className="preview-item">
                        <div className="preview-label">Cost Price</div>
                        <div className="preview-value">
                          ₹ {variant.cost_price}
                        </div>
                      </div>

                      <div className="preview-item">
                        <div className="preview-label">Max Retail Price</div>
                        <div className="preview-value">₹ {variant.mrp}</div>
                      </div>

                      <div className="preview-item">
                        <div className="preview-label">Sell Price</div>
                        <div className="preview-value">₹ {variant.price}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Helper function to show toast messages (you'll need to implement this)
  const showToast = (message) => {
    // Simple alert for now, but you can implement a custom toast component
    alert(message);
  };

  return (
    <BasePopup
      visible={visible}
      title={title}
      onCancel={() => {
        setFormValues({});
        setExtraFields([]);
        setCurrent(0);
        cancelHandler();
      }}
      onSave={saveProduct}
      saveText={buttonLabel}
      width="500px" /* Use a fixed width instead of percentage */
      maxWidth="500px"
    >
      {popupTitle && (
        <CategoryPopupForm
          title={popupTitle}
          onCancel={() => setPopupTitle("")}
          onSave={saveCategory}
          loading={loading}
          hideCategory={popupTitle === "Add Sub Category"}
          selectedCategoryName={
            popupTitle === "Add Sub Category"
              ? categoryOptions.find((cat) => cat.category_id === categoryId)
                  ?.name || ""
              : ""
          }
        />
      )}

      {!updateProduct && renderSteps()}

      <div className="product-form">
        {(current === 0 || updateProduct) && renderBasicDetailsForm()}
        {current === 1 && renderVariantsForm()}
        {current === 2 && renderPreview()}
      </div>
    </BasePopup>
  );
}

// This would be a separate component for category popup
function CategoryPopupForm({
  title,
  onCancel,
  onSave,
  loading,
  hideCategory,
  selectedCategoryName,
}) {
  const [values, setValues] = useState({ name: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (values.name) {
      onSave(values);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content category-form">
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="close-button" onClick={onCancel}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {hideCategory && selectedCategoryName && (
            <div className="form-group">
              <label>Category</label>
              <input type="text" value={selectedCategoryName} disabled />
            </div>
          )}

          <div className="form-group">
            <label>
              Name <span className="required">*</span>
            </label>
            <input
              type="text"
              value={values.name}
              onChange={(e) => setValues({ ...values, name: e.target.value })}
              placeholder="Enter name"
              required
            />
          </div>

          <div className="modal-footer">
            <button type="button" className="cancel-button" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="submit"
              className="save-button"
              disabled={loading || !values.name}
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
